import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CheckCircleTwoTone, UserOutlined, SettingOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { Spin, Tooltip, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { TOTAL_ROUNDS_MAX } from '../../constants/game';
import useGetRandomUserToken from '../../hooks/useGetRandomUserToken';
import { setMyUserInfoNicknameToCurrentBattle } from '../../redux/actions/battle';
import { updateBattle, updateBattlePlayer, addRoundBatchToBattleRounds, deleteNotPreparedBattlePlayers } from '../../services/firebase';
import {
    findUserFromBattleByRandomTokenId,
    generatePlaceInRadius,
    generateRandomRadius,
    getRandomCzechPlace,
    getRandomPlaceInRegion,
    getUnixTimestamp,
} from '../../util';
import gameModes from '../../enums/modes';

// Type assertion for JS action creator
const setNicknameAction = setMyUserInfoNicknameToCurrentBattle as unknown as (payload: string) => any;

// ============================================
// DESIGN TOKENS
// ============================================
const tokens = {
    colors: {
        surface: 'rgba(18, 18, 20, 0.95)',
        surfaceLight: 'rgba(255, 255, 255, 0.05)',
        surfaceLighter: 'rgba(255, 255, 255, 0.08)',
        border: 'rgba(255, 255, 255, 0.1)',
        borderLight: 'rgba(255, 255, 255, 0.15)',
        text: '#ffffff',
        textMuted: 'rgba(255, 255, 255, 0.6)',
        textDim: 'rgba(255, 255, 255, 0.4)',
        accent: '#c80707',
        accentHover: '#e00808',
        accentGlow: 'rgba(200, 7, 7, 0.4)',
        success: '#22c55e',
        successGlow: 'rgba(34, 197, 94, 0.4)',
        warning: '#f59e0b',
    },
    blur: '24px',
    radius: '16px',
    radiusSmall: '10px',
    shadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
};

// ============================================
// ANIMATIONS
// ============================================
const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const LobbyContainer = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 200;
    width: 420px;
    max-width: 95vw;
    max-height: 90vh;
    animation: ${fadeIn} 0.5s ease-out;

    @media (max-width: 500px) {
        width: 95vw;
    }

    /* Adjust for small height screens (laptops) */
    @media (max-height: 750px) {
        max-height: 95vh;
        top: 50%;
    }

    @media (max-height: 600px) {
        top: 10px;
        transform: translate(-50%, 0);
        max-height: calc(100vh - 20px);
    }
`;

const Panel = styled.div`
    background: ${tokens.colors.surface};
    backdrop-filter: blur(${tokens.blur});
    -webkit-backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radius};
    box-shadow: ${tokens.shadow};
    overflow: hidden;
    max-height: inherit;
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    padding: 20px 24px;
    border-bottom: 1px solid ${tokens.colors.border};
    background: rgba(0, 0, 0, 0.2);
    text-align: center;
    flex-shrink: 0;

    @media (max-height: 650px) {
        padding: 14px 20px;
    }
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0; /* Important for flex child scrolling */

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${tokens.colors.border};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: ${tokens.colors.borderLight};
    }
`;

const Title = styled.h2`
    font-family: 'Work Sans', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: ${tokens.colors.text};
    margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    color: ${tokens.colors.textMuted};
    margin: 0;
`;

const Section = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${tokens.colors.border};

    &:last-child {
        border-bottom: none;
    }

    @media (max-height: 650px) {
        padding: 12px 16px;
    }
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${tokens.colors.textDim};
    margin-bottom: 12px;

    svg {
        font-size: 14px;
    }
`;

const PlayersList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${tokens.colors.border};
        border-radius: 2px;
    }

    @media (max-height: 650px) {
        max-height: 140px;
        gap: 6px;
    }
`;

const PlayerRow = styled.div<{ $isMe?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.15)'
        : tokens.colors.surfaceLight};
    border: 1px solid ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.3)'
        : 'transparent'};
    border-radius: ${tokens.radiusSmall};
`;

const PlayerAvatar = styled.div<{ $isReady?: boolean }>`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${props => props.$isReady
        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
        : 'rgba(255, 255, 255, 0.1)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$isReady ? '#fff' : tokens.colors.textMuted};
    font-size: 16px;
    transition: all 0.3s ease;
`;

const PlayerInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const PlayerName = styled.div<{ $isMe?: boolean }>`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: ${props => props.$isMe ? '600' : '500'};
    color: ${tokens.colors.text};
`;

const PlayerStatus = styled.div<{ $ready?: boolean }>`
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    color: ${props => props.$ready ? tokens.colors.success : tokens.colors.textMuted};
    margin-top: 2px;
`;

const PlayerStatusIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
`;

const NameInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    background: ${tokens.colors.surfaceLight};
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    color: ${tokens.colors.text};
    outline: none;
    transition: all 0.2s ease;

    &:focus {
        border-color: ${tokens.colors.accent};
        background: ${tokens.colors.surfaceLighter};
    }

    &::placeholder {
        color: ${tokens.colors.textDim};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SettingsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const SettingItem = styled.div`
    padding: 10px 14px;
    background: ${tokens.colors.surfaceLight};
    border-radius: ${tokens.radiusSmall};
`;

const SettingLabel = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.colors.textDim};
    margin-bottom: 4px;
`;

const SettingValue = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: ${tokens.colors.text};
`;

const ShareSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: ${tokens.colors.surfaceLight};
    border-radius: ${tokens.radiusSmall};
`;

const ShareUrl = styled.div`
    flex: 1;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: ${tokens.colors.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CopyButton = styled.button<{ $copied?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: ${props => props.$copied ? tokens.colors.success : 'rgba(255, 255, 255, 0.1)'};
    color: ${tokens.colors.text};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.$copied ? tokens.colors.success : 'rgba(255, 255, 255, 0.15)'};
    }
`;

const ButtonsSection = styled.div`
    padding: 20px 24px;
    background: rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;

    @media (max-height: 650px) {
        padding: 14px 20px;
        gap: 8px;
    }
`;

const PrimaryButton = styled.button<{ $disabled?: boolean; $variant?: 'accent' | 'success' }>`
    width: 100%;
    padding: 14px 24px;
    border: none;
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.02em;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    overflow: hidden;

    ${props => props.$disabled ? css`
        background: rgba(80, 80, 80, 0.5);
        color: rgba(255, 255, 255, 0.3);
    ` : props.$variant === 'success' ? css`
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        color: ${tokens.colors.text};
        box-shadow: 0 4px 16px ${tokens.colors.successGlow};

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px ${tokens.colors.successGlow};
        }
    ` : css`
        background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
        color: ${tokens.colors.text};
        box-shadow: 0 4px 16px ${tokens.colors.accentGlow};

        &:hover {
            background: linear-gradient(135deg, ${tokens.colors.accentHover} 0%, #c00707 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 24px ${tokens.colors.accentGlow};
        }
    `}

    &:active {
        transform: translateY(0);
    }

    /* Shimmer effect */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
        );
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover::after {
        opacity: ${props => props.$disabled ? 0 : 1};
        animation: ${shimmer} 1.5s ease-in-out infinite;
    }
`;

const HintText = styled.p`
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    color: ${tokens.colors.textMuted};
    text-align: center;
    margin: 0;

    b {
        color: ${tokens.colors.warning};
    }
`;

const CountdownBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(245, 158, 11, 0.15);
    border-radius: 20px;
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    color: ${tokens.colors.warning};
    margin-top: 8px;
`;

const SliderContainer = styled.div`
    margin-top: 12px;
    padding: 14px;
    background: ${tokens.colors.surfaceLight};
    border-radius: ${tokens.radiusSmall};
`;

const SliderLabel = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

const SliderLabelText = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    color: ${tokens.colors.textMuted};
`;

const SliderValue = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.colors.text};
`;

const StyledSlider = styled.input`
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right,
        ${tokens.colors.accent} 0%,
        ${tokens.colors.accent} var(--progress, 0%),
        rgba(255, 255, 255, 0.1) var(--progress, 0%),
        rgba(255, 255, 255, 0.1) 100%
    );
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${tokens.colors.text};
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        transition: transform 0.15s ease;
    }

    &::-webkit-slider-thumb:hover {
        transform: scale(1.1);
    }

    &::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${tokens.colors.text};
        cursor: pointer;
        border: none;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
`;

const SecondaryButton = styled.button`
    width: 100%;
    padding: 12px 20px;
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radiusSmall};
    background: transparent;
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: ${tokens.colors.textMuted};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${tokens.colors.surfaceLight};
        border-color: ${tokens.colors.borderLight};
        color: ${tokens.colors.text};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

// ============================================
// HELPER FUNCTIONS
// ============================================
const generateRounds = async (currentBattleInfo: any) => {
    const generatedRounds: any[] = [];
    const { mode, battleId, radius, selectedCity, regionNutCode } = currentBattleInfo;

    for (let i = 0; i < TOTAL_ROUNDS_MAX; i++) {
        switch (mode) {
            case gameModes.random: {
                const roundCity = getRandomCzechPlace();
                const roundRadius = generateRandomRadius();
                const panoramaPlace = generatePlaceInRadius(roundRadius, roundCity);
                generatedRounds.push({
                    roundId: i + 1,
                    panoramaPlace,
                    isGuessed: false,
                    guessedTime: 0,
                    city: roundCity,
                });
                break;
            }
            case gameModes.randomRegion: {
                const roundCity = getRandomPlaceInRegion(regionNutCode);
                const roundRadius = generateRandomRadius();
                const panoramaPlace = generatePlaceInRadius(roundRadius, roundCity);
                generatedRounds.push({
                    roundId: i + 1,
                    panoramaPlace,
                    isGuessed: false,
                    guessedTime: 0,
                    city: roundCity,
                });
                break;
            }
            case gameModes.city:
            case gameModes.custom: {
                const panoramaPlace = generatePlaceInRadius(radius, selectedCity);
                generatedRounds.push({
                    roundId: i + 1,
                    panoramaPlace,
                    isGuessed: false,
                    guessedTime: 0,
                    city: selectedCity,
                });
                break;
            }
            default:
        }
    }

    if (generatedRounds.length) {
        return addRoundBatchToBattleRounds(battleId, generatedRounds);
    }

    return null;
};

const getModeLabel = (mode: string) => {
    switch (mode) {
        case gameModes.random: return 'Náhodné místo v ČR';
        case gameModes.randomRegion: return 'Náhodné místo v kraji';
        case gameModes.city: return 'Město';
        case gameModes.custom: return 'Vlastní místo';
        default: return mode;
    }
};

// ============================================
// COMPONENT
// ============================================
export const BattleLobby: React.FC = () => {
    const dispatch = useDispatch();
    const { battleId } = useParams<{ battleId: string }>();
    const randomUserToken = useGetRandomUserToken();
    const inputRef = useRef<HTMLInputElement>(null);

    const currentBattlePlayers = useSelector((state: any) => state.battle.currentBattle.players);
    const currentBattleInfo = useSelector((state: any) => state.battle.currentBattle);

    const [myPlayer, setMyPlayer] = useState<any>();
    const [battleCanBeStarted, setBattleCanBeStarted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [countdownValue, setCountdownValue] = useState(currentBattleInfo.countdown || 60);

    const isBattleCreator = currentBattleInfo.createdById !== null &&
        currentBattleInfo.createdById === randomUserToken;

    const gameCreatorStartButtonDisabled =
        !battleCanBeStarted ||
        !currentBattleInfo.myNickname ||
        currentBattleInfo.myNickname.length < 2;

    // Find my player
    useEffect(() => {
        if (currentBattlePlayers !== null) {
            setMyPlayer(findUserFromBattleByRandomTokenId(currentBattlePlayers, randomUserToken));
        }
    }, [currentBattlePlayers, randomUserToken]);

    // Check if battle can be started
    useEffect(() => {
        const { isGameStarted } = currentBattleInfo;
        if (currentBattlePlayers !== null) {
            const readyPlayers = currentBattlePlayers.filter((player: any) => player.isReady);
            if (
                !isGameStarted &&
                currentBattlePlayers.length > 1 &&
                readyPlayers.length === currentBattlePlayers.length
            ) {
                setBattleCanBeStarted(true);
            } else {
                setBattleCanBeStarted(false);
            }
        }
    }, [currentBattlePlayers, currentBattleInfo]);

    // Focus input on mount
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    // Sync countdown value from Firebase (when creator changes it)
    useEffect(() => {
        if (currentBattleInfo.countdown && currentBattleInfo.countdown !== countdownValue) {
            setCountdownValue(currentBattleInfo.countdown);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBattleInfo.countdown]);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            message.success('Odkaz zkopírován!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            message.error('Nepodařilo se zkopírovat odkaz');
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setNicknameAction(e.target.value));
    };

    const handleReady = async () => {
        if (!battleId) return;

        try {
            await updateBattlePlayer(battleId, randomUserToken, {
                name: currentBattleInfo.myNickname,
                isReady: true,
            });
        } catch (err) {
            console.error('Player ready error:', err);
        }
    };

    const handleStartGame = async () => {
        if (!battleId || isStarting) return;

        setIsStarting(true);
        try {
            await updateBattlePlayer(battleId, randomUserToken, {
                name: currentBattleInfo.myNickname,
            });

            await generateRounds(currentBattleInfo);

            await updateBattle(battleId, {
                currentRoundStart: getUnixTimestamp(new Date()),
                isGameStarted: true,
                round: 1,
            });
        } catch (err) {
            console.error('Start game error:', err);
            setIsStarting(false);
        }
    };

    const handleStartWithoutWaiting = async () => {
        if (!battleId || isStarting) return;

        setIsStarting(true);
        try {
            await updateBattlePlayer(battleId, randomUserToken, {
                name: currentBattleInfo.myNickname,
                isReady: true,
            });

            await generateRounds(currentBattleInfo);

            await updateBattle(battleId, {
                currentRoundStart: getUnixTimestamp(new Date()),
                isGameStarted: true,
                round: 1,
            });

            await deleteNotPreparedBattlePlayers(battleId);
        } catch (err) {
            console.error('Start without waiting error:', err);
            setIsStarting(false);
        }
    };

    const handleCountdownChange = async (value: number) => {
        setCountdownValue(value);
        if (battleId) {
            try {
                await updateBattle(battleId, { countdown: value });
            } catch (err) {
                console.error('Update countdown error:', err);
            }
        }
    };

    const getSliderProgress = () => {
        // min: 15, max: 300
        return ((countdownValue - 15) / (300 - 15)) * 100;
    };

    const players = currentBattlePlayers ?? [];
    const readyCount = players.filter((p: any) => p.isReady || p.userId === currentBattleInfo.createdById).length;

    return (
        <LobbyContainer>
            <Panel>
                <Header>
                    <Title>Multiplayer</Title>
                    <Subtitle>Čekání na hráče...</Subtitle>
                </Header>

                <ScrollableContent>
                {/* Players list */}
                <Section>
                    <SectionTitle>
                        <UserOutlined />
                        Hráči ({players.length})
                    </SectionTitle>
                    <PlayersList>
                        {players.map((player: any, index: number) => {
                            const isMe = player.userId === myPlayer?.userId;
                            const isCreator = player.userId === currentBattleInfo.createdById;
                            const isReady = player.isReady || isCreator;

                            return (
                                <PlayerRow key={player.userId || index} $isMe={isMe}>
                                    <PlayerAvatar $isReady={isReady}>
                                        {isReady ? <CheckOutlined /> : <UserOutlined />}
                                    </PlayerAvatar>
                                    <PlayerInfo>
                                        {isMe ? (
                                            <NameInput
                                                ref={inputRef}
                                                defaultValue={player.name}
                                                placeholder="Tvoje jméno..."
                                                maxLength={20}
                                                disabled={!isBattleCreator && player.isReady}
                                                onChange={handleNameChange}
                                            />
                                        ) : (
                                            <>
                                                <PlayerName>{player.name}</PlayerName>
                                                <PlayerStatus $ready={isReady}>
                                                    {isCreator ? 'Tvůrce hry' : isReady ? 'Připraven' : 'Čeká...'}
                                                </PlayerStatus>
                                            </>
                                        )}
                                    </PlayerInfo>
                                    <PlayerStatusIcon>
                                        {isReady ? (
                                            <CheckCircleTwoTone twoToneColor="#22c55e" />
                                        ) : (
                                            <Spin size="small" />
                                        )}
                                    </PlayerStatusIcon>
                                </PlayerRow>
                            );
                        })}
                    </PlayersList>
                </Section>

                {/* Game settings */}
                <Section>
                    <SectionTitle>
                        <SettingOutlined />
                        Nastavení hry
                    </SectionTitle>
                    <SettingsGrid>
                        <SettingItem>
                            <SettingLabel>Mód</SettingLabel>
                            <SettingValue>{getModeLabel(currentBattleInfo.mode)}</SettingValue>
                        </SettingItem>
                        <SettingItem>
                            <SettingLabel>Počet kol</SettingLabel>
                            <SettingValue>{TOTAL_ROUNDS_MAX}</SettingValue>
                        </SettingItem>
                        {!isBattleCreator && (
                            <SettingItem>
                                <SettingLabel>Čas na tip</SettingLabel>
                                <SettingValue>{currentBattleInfo.countdown}s</SettingValue>
                            </SettingItem>
                        )}
                        <SettingItem>
                            <SettingLabel>Pohyb</SettingLabel>
                            <SettingValue>{currentBattleInfo.noMove ? 'Zakázán' : 'Povolen'}</SettingValue>
                        </SettingItem>
                    </SettingsGrid>

                    {/* Countdown slider for creator */}
                    {isBattleCreator && currentBattleInfo.withCountdown && (
                        <SliderContainer>
                            <SliderLabel>
                                <SliderLabelText>Odpočet po prvním tipu</SliderLabelText>
                                <SliderValue>{countdownValue}s</SliderValue>
                            </SliderLabel>
                            <StyledSlider
                                type="range"
                                min={15}
                                max={300}
                                step={15}
                                value={countdownValue}
                                onChange={(e) => handleCountdownChange(Number(e.target.value))}
                                style={{ '--progress': `${getSliderProgress()}%` } as React.CSSProperties}
                            />
                        </SliderContainer>
                    )}
                </Section>

                {/* Share link */}
                <Section>
                    <SectionTitle>
                        <CopyOutlined />
                        Pozvat hráče
                    </SectionTitle>
                    <ShareSection>
                        <ShareUrl>{window.location.href}</ShareUrl>
                        <Tooltip title={copied ? 'Zkopírováno!' : 'Kopírovat odkaz'}>
                            <CopyButton onClick={handleCopyLink} $copied={copied}>
                                {copied ? <CheckOutlined /> : <CopyOutlined />}
                            </CopyButton>
                        </Tooltip>
                    </ShareSection>
                </Section>
                </ScrollableContent>

                {/* Action buttons */}
                <ButtonsSection>
                    {isBattleCreator ? (
                        <>
                            <PrimaryButton
                                $disabled={gameCreatorStartButtonDisabled || isStarting}
                                disabled={gameCreatorStartButtonDisabled || isStarting}
                                onClick={handleStartGame}
                                $variant="success"
                            >
                                {isStarting ? 'Spouštím...' : 'Zahájit hru'}
                            </PrimaryButton>
                            {!battleCanBeStarted && players.length > 1 && (
                                <>
                                    <HintText>
                                        <b>⚠️</b> Čekám až budou všichni hráči připraveni ({readyCount}/{players.length})
                                    </HintText>
                                    <SecondaryButton
                                        onClick={handleStartWithoutWaiting}
                                        disabled={isStarting || !currentBattleInfo.myNickname || currentBattleInfo.myNickname.length < 2}
                                    >
                                        Začít bez čekání na ostatní
                                    </SecondaryButton>
                                    <HintText style={{ marginTop: '4px' }}>
                                        Hráči, co nejsou připraveni, budou ze hry vyhozeni
                                    </HintText>
                                </>
                            )}
                            {players.length === 1 && (
                                <HintText>
                                    Sdílej odkaz a počkej na další hráče
                                </HintText>
                            )}
                        </>
                    ) : (
                        <>
                            <PrimaryButton
                                $disabled={myPlayer?.isReady || !currentBattleInfo.myNickname || currentBattleInfo.myNickname.length < 2}
                                disabled={myPlayer?.isReady || !currentBattleInfo.myNickname || currentBattleInfo.myNickname.length < 2}
                                onClick={handleReady}
                            >
                                {myPlayer?.isReady ? 'Připraven ✓' : 'Jsem připraven'}
                            </PrimaryButton>
                            {myPlayer?.isReady && (
                                <HintText>
                                    Čekám až tvůrce hry zahájí hru...
                                </HintText>
                            )}
                        </>
                    )}

                    <CountdownBadge>
                        ⏱️ Po prvním tipu začne odpočet {currentBattleInfo.countdown} sekund
                    </CountdownBadge>
                </ButtonsSection>
            </Panel>
        </LobbyContainer>
    );
};

export default BattleLobby;
